import { Model, Factory, belongsTo } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import {
  randCompanyName,
  rand,
  randNumber,
  randSoonDate,
  randPastDate,
  randFutureDate,
  randRecentDate,
  randChanceBoolean,
  randQuote,
} from '@ngneat/falso'
import type { SchemaAttributes } from '../types.js'
import type { MockServer, MirageServer } from './server.js'
import {
  PatientHealthPlanStatuses,
  PatientHealthPlanPaymentMethods,
  PatientHealthPlanCancelReasons,
} from '@/composables/patientHealthPlans.js'
import {
  generateFinanceAmount,
  filterable,
  pageable,
  getExistingEntity,
} from './utils.js'
import { roundEven, toNumber } from '@/utils/number.js'

export type PatientHealthPlanData = SchemaAttributes<'PatientHealthPlan'>

export const model = (Model as ModelDefinition<PatientHealthPlanData>).extend({
  client: belongsTo('client'),
  patient: belongsTo('patient', { inverse: 'activeHealthPlans' }),
  lastUsedItem: belongsTo('patientHealthPlanItem', {
    inverse: null,
  }),
  lastConsultation: belongsTo('consultation', { inverse: null }),
})

export const factory = Factory.extend<PatientHealthPlanData>({
  display_name() {
    return randCompanyName()
  },
  status() {
    return rand([
      undefined,
      ...Object.values(PatientHealthPlanStatuses),
    ]) as PatientHealthPlanData['status']
  },
  payment_method() {
    return this.items_left_count || randChanceBoolean({ chanceTrue: 0.9 })
      ? (rand(
          Object.values(PatientHealthPlanPaymentMethods),
        ) as PatientHealthPlanData['payment_method'])
      : undefined
  },
  start_date() {
    if (this.items_left_count || randChanceBoolean({ chanceTrue: 0.9 })) {
      return this.status === PatientHealthPlanStatuses.AppliedFor
        ? randSoonDate().toISOString()
        : randPastDate({ years: 2 }).toISOString()
    }

    return rand([null, undefined])
  },
  end_date() {
    if (this.start_date) {
      switch (this.status) {
        case PatientHealthPlanStatuses.Active:
        case PatientHealthPlanStatuses.AppliedFor:
        case PatientHealthPlanStatuses.Signed:
        case PatientHealthPlanStatuses.Upcoming:
          return randFutureDate({ years: 2 }).toISOString()
        case PatientHealthPlanStatuses.Ended:
        case PatientHealthPlanStatuses.Lapsed:
        case PatientHealthPlanStatuses.Suspended:
        default:
          return randRecentDate({ days: 60 }).toISOString()
      }
    }

    return rand([null, undefined])
  },
  items_left_count() {
    return randChanceBoolean({ chanceTrue: 0.9 })
      ? randNumber({ min: 1, max: 15 })
      : undefined
  },
  monthly_fee() {
    return roundEven(toNumber(generateFinanceAmount({ min: 25, max: 100 })), 2)
  },
  cancel_reason() {
    return this.status === PatientHealthPlanStatuses.Lapsed
      ? (rand([
          ...Object.values(PatientHealthPlanCancelReasons),
        ]) as PatientHealthPlanData['cancel_reason'])
      : null
  },
  cancel_date() {
    return this.cancel_reason ? randPastDate().toISOString() : null
  },
  status_note() {
    return randQuote()
  },
  afterCreate(patientHealthPlan, server) {
    if (!patientHealthPlan.patient) {
      const client = server.create('client', 'withPhoneNumbers')
      const patient = server.create(
        'patient',
        'withRandomTags',
        'withBreed',
        'withInsuranceCompany',
        { client },
      )
      patientHealthPlan.update({
        patient,
        client,
      })
    }

    if (!patientHealthPlan.lastUsedItem) {
      const item = getExistingEntity(server, 'patientHealthPlanItem')
      patientHealthPlan.update({ lastUsedItem: item })
    }

    if (!patientHealthPlan.lastConsultation) {
      const consultation = getExistingEntity(server, 'consultation')
      patientHealthPlan.update({ lastConsultation: consultation })
    }
  },
})

export function seeds(server: MockServer) {
  server.createList('patientHealthPlan', 30)
}

export function routes(server: MockServer) {
  server.get(
    '/patient_health_plans',
    function (this: MirageServer, schema, request) {
      const patientHealthPlans = schema.all('patientHealthPlan')

      // filters
      const filtered = filterable(patientHealthPlans, request, {
        search: (item, value) => {
          const searchTerm = value.toLowerCase()
          const patientName =
            item.patient?.name?.toLowerCase() ||
            item.patient?.official_name?.toLowerCase() ||
            ''
          const clientName = item.client?.full_name?.toLowerCase() || ''
          const displayName = item.display_name?.toLowerCase() || ''

          return (
            patientName.includes(searchTerm) ||
            clientName.includes(searchTerm) ||
            displayName.includes(searchTerm)
          )
        },
        'department.id': () => true,
        status: (item, value) => item.status === value,
        payment_method: (item, value) => item.payment_method === value,
      })

      // then paginate
      const { data, meta } = pageable(filtered, request)

      return {
        ...this.serialize(data),
        meta: {
          pagination: meta,
        },
      }
    },
  )
}
